import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import axios from "axios";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { post } from "~/Services";
import { centralizedHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { fetchMouleSelectionPrivacy } from "../../AirlineImages/FetchModule";
import { appendSasToken } from "../../Imagehelper";
import { usePrivacyPolicyTabOne } from "../TabOne/TabOne.hooks";

export const TabOne = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(undefined);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const [airportOptions, setAirportOptions] = useState<any[]>([]);

  const airportMap = Object.fromEntries(
    (airportOptions || []).map((item: any) => [String(item?.id), (item?.name || "").toLowerCase()])
  );

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const { data: drilldown, refetch: refetchDocument } = usePrivacyPolicyTabOne(id);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  useEffect(() => {
    if (query.get("activeTab") === "tab-1") {
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("activeTab")]);

  const getRecords = async () => {
    setIsLoading(true);
    try {
      const url = `${centralizedHost}/api/privacy-policy/tab-1`;
      const res = await axios.get(url);

      const items = res?.data || [];

      setData(items);
      setSortedData(items);
      setTotalRecords(items.length);
    } catch {
      setData([]);
      setSortedData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    const loadAirports = async () => {
      const data = await fetchMouleSelectionPrivacy();
      setAirportOptions(data);
    };

    loadAirports();
  }, []);

  useEffect(() => {
    let filteredData = [...data];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();

      filteredData = filteredData.filter((item) => {
        const title = String(item.tab_title_english || "").toLowerCase();

        const airportIds = Array.isArray(item.project_airport_id_1)
          ? item.project_airport_id_1
          : item.project_airport_id_1
            ? [item.project_airport_id_1]
            : [];

        const airportNames = airportIds
          .map((id: any) => {
            const airport = airportOptions.find((a) => String(a.id) === String(id));
            return airport?.name?.toLowerCase() || "";
          })
          .join(" ");

        return title.includes(searchLower) || airportNames.includes(searchLower);
      });
    }

    if (selectedPublished !== "") {
      const isPublished = selectedPublished === true;
      filteredData = filteredData.filter((item: any) => item.published === isPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished, airportOptions]);

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  async function handleDeleteRecord(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${centralizedHost}/api/privacy-policy/tab-1/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }
  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Centralized Policies",
      subModule: `TabOneData`,
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "CENTRALIZED",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "CENTRALIZED_privacyPolicyTabOne",
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/centralized/status-information?activeTab=pending");
        } else {
          navigate("/centralized/status-information?activeTab=draft");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  const handleRowClick = (record: any) => {
    setSelectedRowData(record);
    setId(record.id); // Ensure id is set for fetching detailed data
    setDrawerVisible(true);
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Tab Title",
      dataIndex: "tab_title_english",
      key: "tab_title_english",
      render: decodeHTML,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Airport",
    //   dataIndex: "project_airport_id_1",
    //   key: "project_airport_id_1",
    //   render: (value: any) => {
    //     if (!value) return "-";

    //     const airportIds = Array.isArray(value) ? value : [value];

    //     const airportNames = airportIds
    //       .map((id: any) => {
    //         const airport = airportOptions.find((item) => String(item.value) === String(id));
    //         return airport?.label;
    //       })
    //       .filter(Boolean);

    //     return airportNames.length ? airportNames.join(", ") : "-";
    //   },
    //   sorter: true,
    //   sortOrder:
    //     sortOrder === "asc" && orderByName === "project_airport_id_1"
    //       ? "ascend"
    //       : sortOrder === "desc" && orderByName === "project_airport_id_1"
    //         ? "descend"
    //         : undefined,
    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updated",
      key: "updated",
      // render: renderDateColumn,
      render: (value: any, record: any) => {
        const created = record?.created ? moment(record.created).format("YYYY-MM-DD HH:mm:ss") : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        // if both same -> show null for updated
        if (created && updated && created === updated) {
          return null; // you can also return "-" or <Typography.Text>-</Typography.Text>
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updated"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updated"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          () => handleRowClick(record),
          () => navigate(`/centralized/privacyPolicy/tab-1/add?editId=${record.id}`),
          undefined,
          () => {
            setSelectedRowData(record);
            setDeleteModalVisible(true);
          },
          "users"
        ),
    },
  ];

  const handleSort = (column: any) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...sortedData];
    tempData.sort((a, b) => {
      const valA = a[column.dataIndex];
      const valB = b[column.dataIndex];

      if (typeof valA === "string") {
        return order === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      return order === "asc" ? valA - valB : valB - valA;
    });

    setSortedData(tempData);
    setCurrentPage(0);
  };

  const imageKeys = ["pdf_url"];

  const drilldownAny = drilldown as any;

  const dataWithSas = {
    ...drilldown,
    ...Object.fromEntries(imageKeys.map((key) => [key, drilldownAny?.[key] ? appendSasToken(drilldownAny[key]) : ""])),
  };

  return (
    <>
      <DataTable
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleSort(col) } : {}),
        }))}
        tableData={currentPageData}
        isLoading={isLoading}
        searchPlaceholderText="Search Title"
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        multiSelect={false}
        className="table-clickable"
        hideExport
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />

      <Pagination
        current={currentPage + 1}
        total={totalRecords}
        pageSize={pageSize}
        onChange={(page, newSize) => {
          setCurrentPage(page - 1);
          if (newSize !== pageSize) {
            setPageSize(newSize);
            setCurrentPage(0);
          }
        }}
        showSizeChanger
        pageSizeOptions={["10", "20", "50", "100"]}
        style={{ textAlign: "right" }}
      />

      <TableDrawer
        title={<Typography.Title level={4}>Detailed View</Typography.Title>}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        // fields={fields.TabOneData}
        fields={_get(drilldownData, _get(drilldown, "subModule", "TabOneData"))}
        data={dataWithSas}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={handleDeleteRecord}
        defaultReviewNotes="Kindly review this Policies entry"
        confirmationText="Are you sure you want to delete this Tab Title? Deleting this Tab Title will also permanently remove all Tab Paragraph associated with it in MYairports."
      />
    </>
  );
};

export default TabOne;
